import { Box, Button, Stack, Text } from "@/theme/components";
import { Icons, type SxProps, alpha } from "@/theme/index";
import { useScreenSize } from "@/utils/useScreenSize";
import type { ReactNode } from "react";
import { IconContainer } from "../index";

export interface ReportCardProps {
  title: string | ReactNode;
  description?: string | ReactNode;
  reportType:
    | "person"
    | "phone"
    | "email"
    | "property"
    | "vehicle"
    | "username"
    | "neighborhood"
    | "obituary"
    | "contact"
    | "ancestor"
    | "unclaimed-money"
    | "phone-fraud"
    | "email-fraud"
    | "ip-fraud"
    | "url-fraud";
  clickAction?: () => void;
  actionLabel?: string;
  sx?: SxProps;
}

const ICONSBYREPORT = {
  person: <Icons.Person color="primary" fontSize="small" />,
  phone: <Icons.PhoneIphone color="primary" fontSize="small" />,
  email: <Icons.Email color="primary" fontSize="small" />,
  property: <Icons.Home color="primary" fontSize="small" />,
  vehicle: <Icons.DirectionsCar color="primary" fontSize="small" />,
  username: <Icons.AlternateEmail color="primary" fontSize="small" />,
  neighborhood: <Icons.MapsHomeWork color="primary" fontSize="small" />,
  obituary: <Icons.LocalFlorist color="primary" fontSize="small" />,
  contact: <Icons.BusinessCenter color="primary" fontSize="small" />,
};

export function ReportCard({
  title,
  description,
  reportType,
  clickAction,
  actionLabel,
  sx,
}: ReportCardProps) {
  const { isMobile } = useScreenSize();

  function onCardClick() {
    if (!clickAction) {
      return;
    }
    clickAction();
  }

  function getIconByReportType(reportType: string) {
    return ICONSBYREPORT[reportType as keyof typeof ICONSBYREPORT];
  }

  const icon = getIconByReportType(reportType);

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      onClick={onCardClick}
      px={1.5}
      py={2}
      mb={1.5}
      sx={{
        borderRadius: "12px",
        border: (theme) => `1px solid ${theme.palette.divider}`,
        backgroundColor: (theme) => theme.palette.background.paper,
        ":hover": {
          backgroundColor: (theme) =>
            clickAction ? alpha(theme.palette.primary.light, 0.3) : "auto",
          borderColor: (theme) =>
            clickAction ? theme.palette.primary.main : "auto",
          cursor: clickAction ? "pointer" : "auto",
        },
        ...sx,
      }}
    >
      <Box width={"100%"}>
        <Stack direction={"row"} spacing={2}>
          {icon ? <IconContainer icon={icon} /> : null}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent={"space-between"}
            width={"100%"}
            pt={0.7}
          >
            <Box width={"100%"} position={"relative"}>
              <Text
                variant="h5"
                display={"flex"}
                alignItems={"flex-start"}
                sx={{
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  lineHeight: "24px",
                  mb: 0.5,
                }}
              >
                {title}{" "}
              </Text>
              <Box sx={{ wordBreak: "break-word" }}>{description}</Box>
            </Box>
            {!isMobile && (
              <Box mt={{ xs: 1.5, sm: 0 }} minWidth={"10%"}>
                <Button variant="contained" fullWidth>
                  {actionLabel || "View"}
                </Button>
              </Box>
            )}
          </Stack>
        </Stack>

        {isMobile && (
          <Box mt={{ xs: 1.5, sm: 0 }}>
            <Button variant="contained" fullWidth>
              {actionLabel || "View"}
            </Button>
          </Box>
        )}
      </Box>
    </Stack>
  );
}
